/** Admin → Location Service → Refunds: every refund with its status (Initiated → Processing → Refunded / Failed),
 * item refunds raised by picking waiting to be processed, and (with gateway refunds on) a refund for a payment that
 * never became an order. Refunds for a specific order are created from its order page. */
import { useEffect, useState } from "react";
import { RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "../../../lib/api";
import { inr, useLse } from "../../../lib/lse";
import { Btn, Empty, ErrorBox, Field, Loading, Modal, PageHead, Pill, ResponsiveList, errText, inputCls } from "./LseUI";

const TONE = { INITIATED: "amber", PROCESSING: "blue", REFUNDED: "green", FAILED: "red" };

export default function LseRefunds() {
    const { has } = useLse();
    const [status, setStatus] = useState("");
    const [rows, setRows] = useState(null);
    const [err, setErr] = useState(null);
    const [failed, setFailed] = useState(false);
    const load = () => { setErr(null); api.get(`/admin/lse/refunds${status ? `?status=${status}` : ""}`).then(({ data }) => setRows(data.refunds || [])).catch((e) => setErr(errText(e))); };
    useEffect(load, [status]);
    const act = async (r, kind) => {
        try { await api.post(`/admin/lse/refunds/${r.id}/${kind}`); toast.success(kind === "process" ? "Refund processed" : "Status refreshed"); load(); }
        catch (e) { toast.error(errText(e)); }
    };
    const actions = (r) => (
        <span className="inline-flex gap-2">
            {r.status === "INITIATED" && <Btn className="!px-3 !min-h-[34px]" onClick={() => act(r, "process")} data-testid={`lse-refund-process-${r.id}`}>Process</Btn>}
            {r.status === "PROCESSING" && r.method === "GATEWAY" && has("gateway_refunds") && <Btn tone="ghost" className="!px-3 !min-h-[34px]" onClick={() => act(r, "sync")}><RefreshCcw size={14} /> Check</Btn>}
        </span>
    );
    return (
        <div data-testid="lse-refunds">
            <PageHead title="Refunds" sub="Full, partial and item refunds, to the customer's wallet or back through Razorpay. Create one from the order's page."
                      action={has("gateway_refunds") ? <Btn tone="ghost" onClick={() => setFailed(true)}>Refund a failed payment</Btn> : null} />
            <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
                {["", "INITIATED", "PROCESSING", "REFUNDED", "FAILED"].map((s) => (
                    <button key={s || "all"} type="button" onClick={() => setStatus(s)} className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold border ${status === s ? "bg-foreground text-background border-foreground" : "border-border"}`}>{s ? s.toLowerCase() : "All"}</button>
                ))}
            </div>
            {err && <ErrorBox message={err} onRetry={load} />}
            {!rows && !err && <Loading />}
            {rows?.length === 0 && <Empty>No refunds{status ? ` with status ${status.toLowerCase()}` : ""}.</Empty>}
            {rows?.length > 0 && (
                <ResponsiveList testid="lse-refund-list" rows={rows}
                    columns={[
                        { key: "order", label: "Order", render: (r) => r.order_id ? <Link className="font-semibold text-primary" to={`/admin/orders/${r.order_id}`}>{r.order_code || r.order_id.slice(0, 8)}</Link> : <span className="text-muted-foreground">payment {r.gateway?.payment_id}</span> },
                        { key: "amount", label: "Amount", render: (r) => <span className="tabular-nums font-semibold">{inr(r.amount)}</span> },
                        { key: "kind", label: "Kind", render: (r) => `${r.kind.toLowerCase().replace(/_/g, " ")} · ${r.method === "WALLET" ? "wallet" : "Razorpay"}` },
                        { key: "reason", label: "Reason", render: (r) => <span className="line-clamp-2">{r.reason}</span> },
                        { key: "status", label: "Status", render: (r) => <Pill tone={TONE[r.status]}>{r.status.toLowerCase()}</Pill> },
                        { key: "at", label: "Created", render: (r) => new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) },
                        { key: "a", label: "", className: "text-right", render: actions },
                    ]}
                    card={(r) => (
                        <div>
                            <div className="flex items-center justify-between gap-2"><span className="font-semibold">{inr(r.amount)} · {r.order_code || "payment"}</span><Pill tone={TONE[r.status]}>{r.status.toLowerCase()}</Pill></div>
                            <p className="text-xs text-muted-foreground mt-1">{r.kind.toLowerCase()} · {r.method === "WALLET" ? "wallet" : "Razorpay"} · {r.reason}</p>
                            <div className="mt-2">{actions(r)}</div>
                        </div>
                    )}
                />
            )}
            {failed && <FailedPaymentModal onClose={() => setFailed(false)} onDone={() => { setFailed(false); load(); }} />}
        </div>
    );
}

function FailedPaymentModal({ onClose, onDone }) {
    const [f, setF] = useState({ payment_id: "", amount: "", reason: "" });
    const save = async () => {
        try { const { data } = await api.post("/admin/lse/refunds/failed-payment", { ...f, amount: Number(f.amount) }); toast.success(`Refund ${data.status.toLowerCase()}`); onDone(); }
        catch (e) { toast.error(errText(e)); }
    };
    return (
        <Modal title="Refund a failed payment" onClose={onClose} footer={<><Btn tone="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={!f.payment_id || !(Number(f.amount) > 0) || f.reason.length < 3}>Refund</Btn></>}>
            <p className="text-xs text-muted-foreground">For money captured by Razorpay when no order was created. Refunds go through your store's own Razorpay account.</p>
            <div className="mt-3 grid gap-3">
                <Field label="Razorpay payment id"><input className={inputCls} value={f.payment_id} onChange={(e) => setF({ ...f, payment_id: e.target.value.trim() })} placeholder="pay_…" /></Field>
                <Field label="Amount (₹)"><input className={inputCls} type="number" min="1" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
                <Field label="Reason"><input className={inputCls} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field>
            </div>
        </Modal>
    );
}
